import { ChangeEventHandler, MouseEventHandler } from 'react';

import { useRadioGroupContext } from '../Context';

export interface UseRadioOption {
  disabled?: boolean;
}

const useRadioOption = ({ disabled }: UseRadioOption) => {
  const { canUncheck, value: contextValue, setValue: setContextValue, emitChange } = useRadioGroupContext();

  const notifyAndSet = (newVal: string | null) => {
    setContextValue(newVal);
    // notify the external consumer (group's onChange), if set
    try {
      emitChange?.(newVal);
    } catch (e) {
      // swallow — safety
      // (emitChange comes from consumer code; we don't want to crash UI)
    }
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (disabled) return;

    const currentValue = event.target.value;

    // native change means selection moved to currentValue
    if (contextValue === currentValue && canUncheck) {
      // defensive: change shouldn't fire for already-checked, but keep parity
      notifyAndSet(null);
      return;
    }

    notifyAndSet(currentValue);
  };

  const handleInputClick: MouseEventHandler<HTMLInputElement> = (event) => {
    if (disabled) return;

    const currentValue = event.currentTarget.value;

    // clicking an already-checked radio triggers click even if change doesn't fire
    if (contextValue === currentValue && canUncheck) {
      notifyAndSet(null);
      return;
    }

    // otherwise selection will happen, so set it
    notifyAndSet(currentValue);
  };

  return {
    contextValue,
    handleChange,
    handleInputClick,
  };
};

export default useRadioOption;
